import { AgentInstance } from '@/types/agent';
import { AgentState } from '@/constants/agentStates';
import { create } from 'zustand';
import { WorkflowInstance as WorkflowInstanceFromAgent } from '@/types/agent';

// details returned after successful oauth
interface AuthDetails {
  accessToken: string,
  refreshToken: string,
  userId: string,
  userName: string,
  userEmail: string,
}

interface WorkflowInstance {
  display_name: string;
  tenant_id: string;
  workflow_id: string;
  workflow_version_id: string;
  settings: Record<string, unknown>;
  id: number;
  created_at: string;
  updated_at: string;
  agent_instance_id: string; 
}

interface TenantDetails { 
  tenant: {
    id: string;
    display_name: string;
    slug: string;
    agent: string;
    type: 'USER' | 'AGENCY' | null;
    clerk_org_id: string;
    parent_tenant_id: string | null;
    is_onboarded: boolean;
    settings: Record<string, unknown>;
    created_at: string;
    updated_at: string;
  }
}

interface AppState {
  // Tenant details
  tenantDetails: TenantDetails | null;
  setTenantDetails: (details: TenantDetails) => void;
  // Agent instance details
  agentInstances: AgentInstance[];
  setAgentInstances: (agentInstances: AgentInstance[]) => void;
  authDetails: AuthDetails;
  setAuthDetails: (authDetails: AuthDetails) => void;
  // Active workflow instance
  // TODO: Unify WorkflowInstance types
  activeWorkflowInstance: WorkflowInstance | WorkflowInstanceFromAgent | null;
  setActiveWorkflowInstance: (workflowInstance: WorkflowInstance | WorkflowInstanceFromAgent | null) => void;
  // Current agent state
  currentAgentState: AgentState;
  setCurrentAgentState: (state: AgentState) => void;
  // Workflow execution tracking
  workflowExecutionId: string | null;
  setWorkflowExecutionId: (id: string | null) => void;
  // Workflow input key tracking
  workflowInputKey: string | null;
  setWorkflowInputKey: (key: string | null) => void;
  // New analysis flag - tracks when user explicitly wants to start fresh
  userRequestedNewAnalysis: boolean;
  setUserRequestedNewAnalysis: (requested: boolean) => void;
  // Chat history sidebar state
  isChatHistoryOpen: boolean;
  openChatHistory: () => void;
  closeChatHistory: () => void;
  // Initial messages for newly created chats (before server has chat history)
  chatInitialMessages: Record<string, string>;
  setChatInitialMessage: (executionId: string, message: string) => void;
}

export const useAppStore = create<AppState>((set) => ({
  // Tenant details
  tenantDetails: null,
  setTenantDetails: (details) => set({ tenantDetails: details }),
  // Agent instance details
  agentInstances: [],
  setAgentInstances: (agentInstances) => set({ agentInstances }),
  authDetails: {
    userId: '',
    userName: '',
    userEmail: '',
    accessToken: '',
    refreshToken: '',
  },
  setAuthDetails: (authDetails: AuthDetails) => set({ authDetails }),
  // Active workflow instance
  activeWorkflowInstance: null,
  setActiveWorkflowInstance: (workflowInstance) => set({ activeWorkflowInstance: workflowInstance }),
  // Current agent state
  currentAgentState: AgentState.Idle,
  setCurrentAgentState: (state) => set({ currentAgentState: state }),
  // Workflow execution tracking
  workflowExecutionId: null,
  setWorkflowExecutionId: (id) => set({ workflowExecutionId: id }),
  // Workflow input key tracking
  workflowInputKey: null,
  setWorkflowInputKey: (key) => set({ workflowInputKey: key }),
  // New analysis flag
  userRequestedNewAnalysis: false,
  setUserRequestedNewAnalysis: (requested) => set({ userRequestedNewAnalysis: requested }),
  // Chat history sidebar state
  isChatHistoryOpen: false,
  openChatHistory: () => set({ isChatHistoryOpen: true }),
  closeChatHistory: () => set({ isChatHistoryOpen: false }),
  // Initial messages for newly created chats
  chatInitialMessages: {},
  setChatInitialMessage: (executionId, message) =>
    set((state) => ({
      chatInitialMessages: { ...state.chatInitialMessages, [executionId]: message },
    })),
}));
